export interface CatalogCategoryFlat {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  productCount?: number;
  sortOrder?: number;
}

export interface CatalogCategoryNode extends CatalogCategoryFlat {
  children: CatalogCategoryNode[];
}

function sortNodes(nodes: CatalogCategoryNode[]) {
  nodes.sort((a, b) => {
    const ao = a.sortOrder ?? 0;
    const bo = b.sortOrder ?? 0;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
  for (const node of nodes) sortNodes(node.children);
}

export function buildCategoryTree(flat: CatalogCategoryFlat[]): CatalogCategoryNode[] {
  const byId = new Map<string, CatalogCategoryNode>();
  for (const row of flat) {
    byId.set(row.id, { ...row, children: [] });
  }
  const roots: CatalogCategoryNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  sortNodes(roots);
  return roots;
}

export function indexCategories(flat: CatalogCategoryFlat[]): Map<string, CatalogCategoryFlat> {
  return new Map(flat.map((c) => [c.id, c]));
}

/** Keep nodes that have shop products in this category or any descendant. */
export function pruneShopCategoryTree(nodes: CatalogCategoryNode[]): CatalogCategoryNode[] {
  const out: CatalogCategoryNode[] = [];
  for (const node of nodes) {
    const children = pruneShopCategoryTree(node.children);
    const selfCount = node.productCount ?? 0;
    if (selfCount > 0 || children.length > 0) {
      out.push({ ...node, children });
    }
  }
  return out;
}

export function findCategoryPath(
  flat: CatalogCategoryFlat[],
  categoryId: string | null | undefined,
): CatalogCategoryFlat[] {
  if (!categoryId) return [];
  const byId = indexCategories(flat);
  const path: CatalogCategoryFlat[] = [];
  let current = byId.get(categoryId);
  while (current) {
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
}

export function formatCategoryPath(path: CatalogCategoryFlat[], separator = " › "): string {
  return path.map((p) => p.name).join(separator);
}

export function collectAncestorIds(flat: CatalogCategoryFlat[], categoryId: string | null): Set<string> {
  const open = new Set<string>();
  if (!categoryId) return open;
  const byId = indexCategories(flat);
  let current = byId.get(categoryId);
  while (current?.parentId) {
    open.add(current.parentId);
    current = byId.get(current.parentId);
  }
  return open;
}
